import { useGSAP } from "@gsap/react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import React from "react";
import VariableImage from "@/components/ui/VariableImage";
import { useBreakpoint } from "@/lib/breakpoints";
import { cn } from "@/lib/utils";
import { useIntroStore } from "@/store/introStore";

gsap.registerPlugin(useGSAP, ScrollTrigger);

type Props = {
  imgs: string[];
  title: string;
  text: string;
};

const ImgsHeroSection: React.FC<Props> = ({ imgs, title, text }) => {
  const { isDesktop } = useBreakpoint();
  const introDone = useIntroStore((state) => state.introDone);

  useGSAP(
    () => {
      if (!introDone) return;

      const diningHeroImgs: HTMLImageElement[] =
        gsap.utils.toArray("#diningHero img");
      const heroTl = gsap.timeline({
        defaults: { ease: "power2.inOut", duration: 1.5 },
      });

      diningHeroImgs.forEach((img, i) => {
        heroTl.from(
          img,
          {
            clipPath: isDesktop
              ? gsap.utils.random([
                  "inset(100% 0 0 0)",
                  "inset(0 100% 0 0)",
                  "inset(0 0 100% 0)",
                  "inset(0 0 0 100%)",
                ])
              : "none",
            opacity: 0,
          },
          0,
        );
      });

      heroTl.from(
        "#diningHero :is(h1,p,.seperator)",
        {
          y: 50,
          autoAlpha: 0,
          stagger: 0.1,
        },
        "<",
      );
      ScrollTrigger.refresh();
    },
    { dependencies: [isDesktop, introDone], revertOnUpdate: true },
  );
  const classNames = [
    "top-36 inset-s-[5%] lg:inset-s-[15%] aspect-square max-w-[clamp(100px,12vw,400px)]",
    "top-36 inset-e-0 aspect-[2/1.1] max-w-[clamp(200px,30vw,500px)]",
    "-bottom-40 inset-e-[15%] aspect-square max-w-[clamp(200px,12vw,400px)] hidden lg:block",
    "-bottom-40 lg:inset-s-20 aspect-[2/1.4] max-w-[clamp(250px,30vw,500px)]",
    "-bottom-40 lg:bottom-0 inset-s-0 aspect-square max-w-[clamp(150px,12vw,400px)]",
  ];
  return (
    <section
      id="diningHero"
      className="container relative min-h-screen flex-center flex-col pt-[40vh] pb-20 lg:pt-[30vh]"
    >
      <h1 className="title-70 uppercase! z-2 lg:max-w-[700px]">{title}</h1>
      <p className="z-2 mt-5 whitespace-pre-line lg:ms-[50vw] lg:mt-0 lg:max-w-[500px]">
        {text}
      </p>

      {imgs.map((img: any, i) => (
        <div key={i} className={cn("absolute z-1 w-full", classNames[i] || "")}>
          <VariableImage
            src={typeof img !== "string" ? img.attributes.url : img}
            alt={img.attributes.alternativeText}
            caption={img.attributes.caption}
            fill={true}
            sizes="40vw"
            className="relative object-cover object-center"
          />
        </div>
      ))}
    </section>
  );
};

export default ImgsHeroSection;
